import React, { useState } from 'react';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  InsertThematicBreak,
  linkPlugin,
  linkDialogPlugin,
} from '@mdxeditor/editor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, Box } from '@mui/material';

// eslint-disable-next-line import/no-unresolved
import '@mdxeditor/editor/style.css';
import { theme } from '../../../../theme/default';
import { onClickTimestamp } from '../../../../lib/commonUtils';

interface TemplateEditorProps {
  content: string;
  onChange?: (content: string) => void;
  readOnly?: boolean;
  searchTerm?: string;
  hideReferences?: boolean;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  content,
  onChange,
  readOnly = false,
  searchTerm = '',
  hideReferences = false,
}) => {
  const [activeCitation, setActiveCitation] = useState<number | null>(null);

  const plugins = [
    headingsPlugin(),
    listsPlugin(),
    quotePlugin(),
    thematicBreakPlugin(),
    markdownShortcutPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    toolbarPlugin({
      toolbarContents: () => (
        <>
          <UndoRedo />
          <BoldItalicUnderlineToggles />
          <BlockTypeSelect />
          <CreateLink />
          <InsertThematicBreak />
        </>
      ),
    }),
  ];

  const highlightSearchTerm = (
    text: string | React.ReactNode
  ): React.ReactNode => {
    if (!searchTerm || typeof text !== 'string') return text;
    try {
      const escapedSearchTerm = searchTerm.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
      return text.split(regex).map((part, index) =>
        regex.test(part) ? (
          <mark
            key={index}
            style={{
              backgroundColor: '#fff176',
              color: '#000',
              borderRadius: '2px',
              padding: '0 2px',
            }}
          >
            {part}
          </mark>
        ) : (
          part
        )
      );
    } catch (error) {
      console.error('Search highlighting error:', error);
      return text;
    }
  };

  if (readOnly) {
    return (
      <div className="readonly-editor">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children, ...props }) => (
              <h1 style={{ fontSize: '1.5em' }} {...props}>
                {highlightSearchTerm(children)}
              </h1>
            ),
            h2: ({ children, ...props }) => (
              <h2 style={{ fontSize: '1.3em' }} {...props}>
                {highlightSearchTerm(children)}
              </h2>
            ),
            h3: ({ children, ...props }) => (
              <h3 style={{ fontSize: '1.1em' }} {...props}>
                {highlightSearchTerm(children)}
              </h3>
            ),
            h4: ({ children, ...props }) => (
              <h4 style={{ fontSize: '1em' }} {...props}>
                {highlightSearchTerm(children)}
              </h4>
            ),
            h5: ({ children, ...props }) => (
              <h5 style={{ fontSize: '0.9em' }} {...props}>
                {highlightSearchTerm(children)}
              </h5>
            ),
            h6: ({ children, ...props }) => (
              <h6 style={{ fontSize: '0.8em' }} {...props}>
                {highlightSearchTerm(children)}
              </h6>
            ),
            p: ({ children, ...props }) => (
              <p {...props}>{highlightSearchTerm(children)}</p>
            ),
            li: ({ children, ...props }) => (
              <li {...props}>{highlightSearchTerm(children)}</li>
            ),
            table: ({ children, ...props }) => (
              <table
                style={{ borderCollapse: 'collapse', width: '100%' }}
                {...props}
              >
                {children}
              </table>
            ),
            th: ({ children, ...props }) => (
              <th
                style={{
                  border: `1px solid ${theme.palette.secondary.light}`,
                  padding: '8px',
                  backgroundColor: theme.palette.secondary.light,
                }}
                {...props}
              >
                {highlightSearchTerm(children)}
              </th>
            ),
            td: ({ children, ...props }) => (
              <td
                style={{
                  border: `1px solid ${theme.palette.secondary.light}`,
                  padding: '8px',
                }}
                {...props}
              >
                {highlightSearchTerm(children)}
              </td>
            ),
            a: ({ href, children }) => {
              if (href?.match(/^\d+(\.\d+)?$/)) {
                if (hideReferences) return null;
                const seconds = parseFloat(href);
                const isActive = activeCitation === seconds;

                return (
                  <Box
                    component="span"
                    onClick={() => {
                      onClickTimestamp(seconds);
                      setActiveCitation(seconds);
                      setTimeout(() => setActiveCitation(null), 1000);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '1.25rem',
                      height: '1.25rem',
                      borderRadius: '50%',
                      backgroundColor: isActive
                        ? 'secondary.dark'
                        : 'secondary.light',
                      border: 1,
                      borderColor: 'secondary.main',
                      color: 'secondary.contrastText',
                      cursor: 'pointer',
                      fontSize: '0.625rem',
                      fontWeight: 500,
                      ml: 0.5,
                      mr: 0.5,
                      verticalAlign: 'baseline',
                      position: 'relative',
                      top: '-0.125em',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      minWidth: '1.25rem',
                      '&:hover': {
                        backgroundColor: isActive
                          ? 'secondary.dark'
                          : 'secondary.main',
                      },
                    }}
                  >
                    →
                  </Box>
                );
              }
              return <Link href={href}>{children}</Link>;
            },
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="editable-editor">
      <MDXEditor
        markdown={content}
        onChange={onChange}
        plugins={plugins}
        contentEditableClassName="editor-content"
      />
    </div>
  );
};

export default TemplateEditor;
